/**
 * Slot machine helper functions
 */

import type { SpinResult, SpinPrizeType } from '../types';
import { SLOT_SYMBOLS, RUMIO_CONFIG } from './constants';

/**
 * Get display info for a spin result
 */
export function getSpinResultDisplay(result: SpinResult): {
  title: string;
  subtitle: string;
  icon: string;
  color: string;
  isWin: boolean;
} {
  switch (result.prize_type) {
    case 'nothing':
      return {
        title: 'Better luck next time!',
        subtitle: 'Come back tomorrow for another spin',
        icon: '🍋',
        color: 'text-yellow-500',
        isWin: false,
      };

    case 'xp':
      return {
        title: `+${result.xp_won} XP!`,
        subtitle: 'You earned experience points',
        icon: '✨',
        color: 'text-green-500',
        isWin: true,
      };

    case 'super':
    case 'fresh':
    case 'standard': {
      const config = RUMIO_CONFIG[result.prize_type as 'super' | 'fresh' | 'standard'];
      const plural = result.rumios_won > 1 ? config.namePlural : config.name;
      return {
        title: `${result.rumios_won} ${plural}!`,
        subtitle: result.multiplier > 1
          ? `Base: ${result.base_rumios} × ${result.multiplier}x multiplier`
          : 'Use it to unlock mystery boxes',
        icon: config.icon,
        color: config.color,
        isWin: true,
      };
    }

    case 'rumios': {
      // Unified rumios (uses gold ring emoji for text display)
      return {
        title: `${result.rumios_won} Rumios!`,
        subtitle: result.multiplier > 1
          ? `Base: ${result.base_rumios} × ${result.multiplier}x multiplier`
          : 'Use them to unlock mystery boxes',
        icon: '💍',
        color: 'text-amber-500',
        isWin: true,
      };
    }

    case 'pro_pass':
      return {
        title: '24hr Pro Pass!',
        subtitle: 'Enjoy Pro benefits for the next 24 hours',
        icon: '👑',
        color: 'text-amber-600',
        isWin: true,
      };

    default:
      return {
        title: 'Spin complete!',
        subtitle: '',
        icon: '🎰',
        color: 'text-gray-500',
        isWin: false,
      };
  }
}

/**
 * Get slot reel symbols for animation
 * Returns 3 symbols for the final display
 */
export function getSlotReelSymbols(result: SpinResult): string[] {
  switch (result.prize_type) {
    case 'super':
      return ['⭐', '⭐', '⭐'];
    case 'fresh':
      return ['💎', '💎', '💎'];
    case 'standard':
      return ['🎟️', '🎟️', '🎟️'];
    case 'rumios':
      return ['💍', '💍', '💍'];
    case 'xp':
      return ['✨', '✨', '✨'];
    case 'pro_pass':
      return ['👑', '👑', '👑'];
    case 'nothing':
    default:
      // Return a losing combination
      return ['🍋', '🎟️', '🍋'];
  }
}

/**
 * Generate random symbols for slot animation
 */
export function getRandomSlotSymbols(): string[] {
  const symbols = Object.values(SLOT_SYMBOLS).map((s) => s.symbol);
  return [
    symbols[Math.floor(Math.random() * symbols.length)],
    symbols[Math.floor(Math.random() * symbols.length)],
    symbols[Math.floor(Math.random() * symbols.length)],
  ];
}

/**
 * Get multiplier display text
 */
export function getMultiplierDisplay(multiplier: number): {
  text: string;
  color: string;
} {
  if (multiplier >= 2) {
    return {
      text: `${multiplier}x`,
      color: 'text-amber-500',
    };
  } else if (multiplier > 1) {
    return {
      text: `${multiplier}x`,
      color: 'text-green-500',
    };
  }
  return {
    text: '1x',
    color: 'text-gray-400',
  };
}

/**
 * Get multiplier bonus badges
 */
export function getMultiplierBadges(
  hasExtension: boolean,
  hasApp: boolean
): Array<{ label: string; icon: string; active: boolean }> {
  return [
    {
      label: 'Extension',
      icon: '🧩',
      active: hasExtension,
    },
    {
      label: 'Mobile App',
      icon: '📱',
      active: hasApp,
    },
  ];
}

/**
 * Calculate potential winnings with multiplier
 */
export function calculatePotentialWinnings(
  baseAmount: number,
  multiplier: number
): number {
  return Math.ceil(baseAmount * multiplier);
}

/**
 * Get prize probability display (for UI)
 */
export function getPrizeProbabilities(): Array<{
  prize: string;
  probability: string;
  icon: string;
}> {
  return [
    { prize: 'Standard Rumio', probability: '~17%', icon: '🎟️' },
    { prize: 'Fresh Rumio', probability: '~11%', icon: '💎' },
    { prize: 'Super Rumio', probability: '~5%', icon: '⭐' },
    { prize: 'XP Bonus', probability: '~27%', icon: '✨' },
    { prize: '24hr Pro Pass', probability: '~1%', icon: '👑' },
  ];
}

/**
 * Format time until next spin
 */
export function getTimeUntilNextSpin(lastSpinDate: string | null): string {
  if (!lastSpinDate) return 'Spin available!';

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  const diffMs = tomorrow.getTime() - Date.now();

  if (diffMs <= 0) return 'Spin available!';

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m until next spin`;
  }
  return `${minutes}m until next spin`;
}

/**
 * Check if spin is available today
 */
export function canSpinToday(lastSpinDate: string | null): boolean {
  if (!lastSpinDate) return true;

  const today = new Date().toISOString().split('T')[0];
  return lastSpinDate !== today;
}

/**
 * Get confetti colors based on prize type
 */
export function getConfettiColors(prizeType: SpinPrizeType): string[] {
  switch (prizeType) {
    case 'super':
      return ['#fbbf24', '#f59e0b', '#d97706']; // Amber/gold
    case 'fresh':
      return ['#a855f7', '#8b5cf6', '#7c3aed']; // Purple
    case 'standard':
      return ['#3b82f6', '#2563eb', '#1d4ed8']; // Blue
    case 'rumios':
      return ['#fbbf24', '#f59e0b', '#d97706']; // Amber/gold (unified)
    case 'xp':
      return ['#22c55e', '#16a34a', '#15803d']; // Green
    case 'pro_pass':
      return ['#fbbf24', '#f59e0b', '#ec4899']; // Gold/pink
    case 'nothing':
    default:
      return ['#9ca3af', '#6b7280', '#4b5563']; // Gray
  }
}
